/**
 * Insight orchestration functions
 * Handles prioritization, deduplication, and overall insight generation
 */

import { ProfitInsight, InsightsAnalysis, InsightType } from '../types/insights';
import { DailyProfitSummary, HourlyProfitBreakdown, AssetBreakdownSummary } from '../api/profit';
import { DateRange } from '../utils/dateUtils';
import {
  analyzeDayOfWeekPerformance,
  analyzeHourOfDayPerformance,
  analyzeAssetPerformance,
  analyzeFeeRatio,
  analyzeTrend,
  analyzeConsecutivePerformance,
  analyzeWinRateTrend,
} from './insightDetectors';

/**
 * Prioritize insights by priority level and estimated impact
 * @param insights - Array of insights to prioritize
 * @returns Sorted array with highest priority first
 */
export function prioritizeInsights(insights: ProfitInsight[]): ProfitInsight[] {
  if (!insights || insights.length === 0) {
    return [];
  }

  // Define priority order
  const priorityOrder = { high: 3, medium: 2, low: 1 };

  return [...insights].sort((a, b) => {
    // First, sort by priority
    const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];
    if (priorityDiff !== 0) {
      return priorityDiff;
    }

    // Within same priority, sort by estimated impact amount (descending)
    return Math.abs(b.estimatedImpact.amount) - Math.abs(a.estimatedImpact.amount);
  });
}

/**
 * Deduplicate insights to prevent redundant or contradictory information
 * @param insights - Array of insights to deduplicate
 * @returns Deduplicated array
 */
export function deduplicateInsights(insights: ProfitInsight[]): ProfitInsight[] {
  if (!insights || insights.length === 0) {
    return [];
  }

  // Use a Map to track unique insights by ID
  const uniqueInsights = new Map<string, ProfitInsight>();

  insights.forEach(insight => {
    const existing = uniqueInsights.get(insight.id);
    
    if (!existing) {
      // New insight, add it
      uniqueInsights.set(insight.id, insight);
    } else {
      // Duplicate found - keep the one with higher confidence or priority
      const keepExisting = 
        existing.confidenceScore > insight.confidenceScore ||
        (existing.confidenceScore === insight.confidenceScore && 
         existing.priority === 'high' && insight.priority !== 'high');
      
      if (!keepExisting) {
        uniqueInsights.set(insight.id, insight);
      }
    }
  });

  return Array.from(uniqueInsights.values());
}

/**
 * Limit insights to top N most important
 * @param insights - Array of insights (should already be prioritized)
 * @param maxCount - Maximum number of insights to return
 * @returns Limited array
 */
export function limitInsights(insights: ProfitInsight[], maxCount: number = 10): ProfitInsight[] {
  if (!insights || insights.length === 0) {
    return [];
  }

  return insights.slice(0, maxCount);
}

/**
 * Data needed for insight generation
 */
export interface InsightGenerationData {
  dailyData: DailyProfitSummary[];
  hourlyData?: HourlyProfitBreakdown[];
  assetBreakdowns?: AssetBreakdownSummary[];
  previousPeriodData?: DailyProfitSummary[];
  dateRange: DateRange;
}

/**
 * Main insights generation engine
 * Orchestrates all detectors and produces final InsightsAnalysis
 * 
 * @param data - All data needed for insight generation
 * @returns Complete insights analysis
 */
export function generateInsights(data: InsightGenerationData): InsightsAnalysis {
  const allInsights: ProfitInsight[] = [];

  // Run all detectors
  try {
    // Day of week performance
    const dayOfWeekInsights = analyzeDayOfWeekPerformance(data.dailyData);
    allInsights.push(...dayOfWeekInsights);

    // Hour of day performance (if hourly data available)
    if (data.hourlyData && data.hourlyData.length > 0) {
      const hourInsights = analyzeHourOfDayPerformance(data.hourlyData);
      allInsights.push(...hourInsights);
    }

    // Asset performance (if breakdown data available)
    if (data.assetBreakdowns && data.assetBreakdowns.length > 0) {
      const assetInsights = analyzeAssetPerformance(data.assetBreakdowns);
      allInsights.push(...assetInsights);
    }

    // Fee ratio
    const feeInsights = analyzeFeeRatio(data.dailyData);
    allInsights.push(...feeInsights);

    // Trend detection
    const trendInsights = analyzeTrend(data.dailyData);
    allInsights.push(...trendInsights);

    // Consecutive performance
    const consecutiveInsights = analyzeConsecutivePerformance(data.dailyData);
    allInsights.push(...consecutiveInsights);

    // Win rate trend (if previous period data available)
    if (data.previousPeriodData && data.previousPeriodData.length > 0) {
      const winRateInsights = analyzeWinRateTrend(data.dailyData, data.previousPeriodData);
      allInsights.push(...winRateInsights);
    }
  } catch (error) {
    console.error('Error generating insights:', error);
    // Continue with whatever insights were successfully generated
  }

  // Deduplicate and prioritize
  const uniqueInsights = deduplicateInsights(allInsights);
  const prioritizedInsights = prioritizeInsights(uniqueInsights);
  
  // Limit to top 10 by default (can be adjusted)
  const finalInsights = limitInsights(prioritizedInsights, 10);

  // Calculate summary statistics
  const opportunityCount = finalInsights.filter(i => i.type === 'opportunity').length;
  const warningCount = finalInsights.filter(i => i.type === 'warning').length;
  const patternCount = finalInsights.filter(i => i.type === 'pattern').length;

  return {
    insights: finalInsights,
    summary: {
      totalInsights: finalInsights.length,
      opportunityCount,
      warningCount,
      patternCount,
      analyzedAt: new Date(),
      dateRange: data.dateRange,
    },
  };
}

/**
 * Filter insights by type
 * @param insights - Array of insights
 * @param type - Type to filter by, or 'all' for no filtering
 * @returns Filtered array
 */
export function filterInsightsByType(
  insights: ProfitInsight[],
  type: InsightType | 'all'
): ProfitInsight[] {
  if (type === 'all') {
    return insights;
  }
  
  return insights.filter(i => i.type === type);
}
